// ════════════════════════════════════════════════════════════════════
// i18n catalog — namespace "app.*"  (strings from frontend/src/App.js)
//
// One entry per user-facing STATIC UI string wrapped with t("app.<key>")
// in App.js. Values are copied VERBATIM from the source for `en`; `ar` is
// accurate Modern Standard Arabic.
//
// SCOPE: static UI copy only — headings, labels, buttons, placeholders,
// empty-states, tooltips, descriptions. NEVER includes dynamic data
// (names/emails/cities), numbers, currency/figures, platform names
// (Talabat/Deliveroo/…), or brand names (Netrev, Stripe, Pro). Numbers and
// symbols inside a string are kept as-is in the Arabic.
//
// The parent merges this module into lib/i18n.js's catalog. Keys here do
// NOT overlap with the existing nav.* / auth.* / btn.* / mobnav.* /
// topbar.* / status.* / plan.* keys already in i18n.js.
// ════════════════════════════════════════════════════════════════════

export const en = {
  // ── Mobile: workspace picker (WsPicker) ──────────────────────────────
  "app.mob.switchWorkspace": "Switch workspace",

  // ── Mobile: dashboard (MobDash) ──────────────────────────────────────
  "app.mob.performance": "Performance",
  "app.mob.grossRevenue": "Gross Revenue",
  "app.mob.netReceivable": "Net Receivable",
  "app.mob.vatPaid": "VAT Paid",
  "app.mob.trueProfit": "True Profit",
  "app.mob.avgMonthlyProfit": "Avg monthly profit",
  "app.mob.trueProfitPerMonth": "True profit / month",
  "app.mob.recentMonths": "Recent months",
  "app.mob.grossSuffix": "gross",

  // ── Mobile: items (MobItems) ─────────────────────────────────────────
  "app.mob.add": "+ Add",
  "app.mob.newItem": "New item",
  "app.mob.itemName": "Item name",
  "app.mob.itemNamePlaceholder": "e.g. Chicken Burger",
  "app.mob.yourCost": "Your cost",
  "app.mob.listingPrice": "Listing price",
  "app.mob.discountTier": "Discount tier",
  "app.mob.none": "None",
  "app.mob.campaignFee": "Campaign fee",
  "app.mob.campaignFeeHint": "Auto-filled from tier · override if needed",
  "app.mob.remove": "Remove",
  "app.mob.cost": "Cost",
  "app.mob.fullBreakdown": "Full breakdown",
  "app.mob.allFormulasVerified": "all formulas verified ✅",
  "app.mob.sellingPrice": "Selling price",
  "app.mob.discount": "Discount",
  "app.mob.campaignDeliveryFee": "Campaign / delivery fee",
  "app.mob.netTicketTotal": "Net ticket total",
  "app.mob.aggregatorAt": "Aggregator @",
  "app.mob.netReceivableFromAgg": "Net receivable from aggregator",
  "app.mob.vatAt": "VAT @",
  "app.mob.profitPerSale": "✓ Profit per sale",
  "app.mob.lossPerSale": "✗ Loss per sale",
  "app.mob.afterCostPrefix": "After cost (£",
  "app.mob.afterCostSuffix": "), tax & all deductions",
  "app.mob.allItems": "All items",
  "app.mob.colItem": "Item",
  "app.mob.colCost": "Cost",
  "app.mob.colPrice": "Price",
  "app.mob.colDisc": "Disc",
  "app.mob.colFee": "Fee",
  "app.mob.colRecv": "Recv.",
  "app.mob.colPL": "P/L",

  // ── Mobile: log (MobLog) ─────────────────────────────────────────────
  "app.mob.logSuffix": "log",
  "app.mob.seeAdminFullHistory": "See Admin for full 5yr history",
  "app.mob.logTodaysSales": "+ Log today's sales",
  "app.mob.newEntry": "New entry",
  "app.mob.date": "Date",
  "app.mob.gross": "Gross",
  "app.mob.netOnTicket": "Net on Ticket",
  "app.mob.cpcSpend": "CPC Spend",
  "app.mob.netAfterDiscComm": "Net after disc & comm",
  "app.mob.cardFees": "Card fees",
  "app.mob.vat": "VAT",
  "app.mob.earningAfterVat": "Earning after VAT",
  "app.mob.cogs": "COGS",
  "app.mob.cogsGrossSuffix": "gross",
  "app.mob.pctEarningOnGross": "% Earning on Gross",
  "app.mob.netProfitPct": "Net Profit %",
  "app.mob.cogsPctOnNet": "COGS % on Net",

  // ── Mobile: account (MobAccount) ─────────────────────────────────────
  "app.mob.totalMonthlyBilling": "Total monthly billing",
  "app.mob.perMonth": "/mo",
  "app.mob.monthlyCharge": "Monthly charge",
  "app.mob.nextBilling": "Next billing",
  "app.mob.manageBilling": "Manage billing",
  "app.mob.addAnotherLocation": "Add another location?",
  "app.mob.ownMenuAgg": "/month · own menu · own aggregator",
  "app.mob.addWorkspace": "+ Add workspace",
  "app.mob.newWorkspace": "New workspace",
  "app.mob.locationName": "Location name",
  "app.mob.locationNamePlaceholder": "e.g. Burger Bite — Leeds",
  "app.mob.aggregatorPlatform": "Aggregator platform",
  "app.mob.confirmSubscription": "Confirm subscription",
  "app.mob.platform": "Platform",
  "app.mob.addedCharge": "Added charge",
  "app.mob.newTotal": "New total",
  "app.mob.redirectingStripe": "🔒 Redirecting to Stripe to authorise this subscription.",
  "app.mob.back": "← Back",
  "app.mob.since": "since",
  "app.mob.itemsSuffix": "items",
  "app.mob.cancel": "Cancel",

  // ── Auth panel (AuthVisualPanel) ─────────────────────────────────────
  "app.auth.realProfitHeadline": "The real profit on every order.",
  "app.auth.realProfitSub": "Track P&L across every delivery platform. See what's actually left after commissions, card fees, VAT, and food cost — daily, per platform.",
  "app.auth.commissionDeducted": "Commission deducted",
  "app.auth.vatCalculated": "VAT calculated",
  "app.auth.trueProfitMargin": "True profit margin",
  "app.auth.supports": "Supports",

  // ── Auth screen (AuthScreen) — strings not already in i18n.js ────────
  "app.auth.unitedKingdom": "United Kingdom",
  "app.auth.billedInGbp": "Billed in GBP",
  "app.auth.uae": "UAE",
  "app.auth.billedInAed": "Billed in AED",
  "app.auth.unitedStates": "United States",
  "app.auth.billedInUsd": "Billed in USD",
  "app.auth.india": "India",
  "app.auth.billedInInr": "Billed in INR",
  "app.view.navigation": "Navigation",
  "app.auth.regionLockNote": "This locks your account's billing currency. You'll create workspaces for this region only.",
  "app.auth.bizRestaurant": "Restaurant",
  "app.auth.bizCloudKitchen": "Cloud kitchen",
  "app.auth.bizPharmacy": "Pharmacy",
  "app.auth.bizSupermarket": "Supermarket",
  "app.auth.bizRetail": "Online retail",
  "app.auth.emailPlaceholder": "Enter your email address",
  "app.auth.namePlaceholder": "Enter your name",
  "app.auth.phonePlaceholder": "Your number",
  "app.auth.passwordPlaceholderSignup": "Create a password (at least 8 characters)",
  "app.auth.passwordPlaceholderLogin": "Enter your password",
  "app.auth.signInArrow": "Sign in →",
  "app.auth.weSentConfirmation": "We've sent a confirmation link to",
  "app.auth.activateLinkExpires": "Click the link to activate your account. The link expires in 24 hours. Check your spam folder if you don't see it.",
  "app.auth.useDifferentEmail": "Use different email",
  "app.auth.signIn": "Sign in",
  "app.auth.ifAccountExistsFor": "If an account exists for",
  "app.auth.weSentResetLink": "we've sent a password reset link.",
  "app.auth.resetLinkExpires": "Click the link in the email to set a new password. The link expires in 1 hour. Check your spam folder if you don't see it within a minute.",
  "app.auth.tryDifferentEmail": "Try different email",
  "app.auth.backToSignIn": "Back to sign in",
  "app.auth.byCreatingAgree": "By creating an account, you agree to our ",
  "app.auth.terms": "Terms",
  "app.auth.and": " and ",
  "app.auth.privacyPolicy": "Privacy Policy",

  // ── Upgrade prompt (UpgradePrompt) ───────────────────────────────────
  "app.upgrade.proFeature": "Pro feature",
  "app.upgrade.featureAvailableSuffix": " is available on the Pro plan. Upgrade to unlock unlimited workspaces, full daily log, CSV import, and more.",
  "app.upgrade.upgradeProCta": "Upgrade to Pro — £49.99/mo",
  "app.upgrade.cancelAnytimeNote": "Cancel anytime · Per workspace · No lock-in",

  // ── Admin: Overview tab (OverviewTab / ChurnList / KpiCard) ──────────
  "app.admin.overviewTitle": "Admin overview",
  "app.admin.overviewDesc": "Headline metrics across all Netrev accounts. Use the cancellation list at the bottom to reach churned and at-risk customers directly.",
  "app.admin.refresh": "Refresh",
  "app.admin.totalAccounts": "Total accounts",
  "app.admin.archivedSuffix": "archived",
  "app.admin.activeAccess": "Active access",
  "app.admin.trialPaidComped": "Trial, paid, or comped",
  "app.admin.cancelled": "Cancelled",
  "app.admin.churnRateSuffix": "churn rate",
  "app.admin.estMrr": "Est. MRR",
  "app.admin.mrrApproxNote": "Approximate · Stripe is source of truth",
  "app.admin.subscriptionMix": "Subscription mix",
  "app.admin.noAccountsYet": "No accounts yet.",
  "app.admin.cancellationsAtRisk": "Cancellations & at-risk accounts",
  "app.admin.churnCancelled": "cancelled",
  "app.admin.churnCancelling": "cancelling",
  "app.admin.churnPastDue": "past due",
  "app.admin.contactOpensEmail": "\"Contact\" opens a pre-written feedback email in your mail app.",
  "app.admin.noCancellationsYet": "No cancellations yet",
  "app.admin.nothingToReachOut": "Nothing to reach out to. Good problem to have.",
  "app.admin.chipCancelled": "Cancelled",
  "app.admin.chipCancelling": "Cancelling",
  "app.admin.chipPastDue": "Past due",
  "app.admin.colAccount": "Account",
  "app.admin.colOwnerEmail": "Owner email",
  "app.admin.colStatus": "Status",
  "app.admin.colPlan": "Plan",
  "app.admin.colDaysActive": "Days active",
  "app.admin.colCancelledApprox": "Cancelled approx.",
  "app.admin.colPeriodEnds": "Period ends",
  "app.admin.contact": "Contact",

  // ── Admin: Accounts tab (AccountsTab) ────────────────────────────────
  "app.admin.accountsTitle": "Accounts",
  "app.admin.accountsDesc": "Every Netrev account, with subscription status, MRR, and workspaces. Click Inspect to see the full read-only snapshot of an account.",
  "app.admin.colOwner": "Owner",
  "app.admin.colPhone": "Phone",
  "app.admin.colSiteNames": "Site names",
  "app.admin.colSites": "Sites",
  "app.admin.colMrr": "MRR",
  "app.admin.colPeriodEndShort": "Period end",
  "app.admin.colJoined": "Joined",
  "app.admin.searchEmailOrAccount": "Search email or account name",
  "app.admin.allStatuses": "All statuses",
  "app.admin.statusTrialing": "Trialing",
  "app.admin.statusActive": "Active",
  "app.admin.statusPastDue": "Past due",
  "app.admin.statusCancelled": "Cancelled",
  "app.admin.statusIncomplete": "Incomplete",
  "app.admin.statusFreeOverride": "Free override",
  "app.admin.activeOnly": "Active only",
  "app.admin.archivedOnly": "Archived only",
  "app.admin.allAccounts": "All accounts",
  "app.admin.newestFirst": "Newest first",
  "app.admin.oldestFirst": "Oldest first",
  "app.admin.emailAZ": "Email A–Z",
  "app.admin.errorPrefix": "Error:",
  "app.admin.loadingEllipsis": "Loading…",
  "app.admin.noAccountsMatchFilters": "No accounts match the current filters.",
  "app.admin.noAccountsMatchColumn": "No accounts on this page match the column filters.",
  "app.admin.badgeFree": "FREE",
  "app.admin.badgeFreeExpired": "FREE · EXPIRED",
  "app.admin.badgeArchived": "ARCHIVED",
  "app.admin.inspect": "Inspect",
  "app.admin.totalSuffix": "total ·",
  "app.admin.showing": "showing",
  "app.admin.prev": "Prev",
  "app.admin.next": "Next",

  // ── Admin: Snapshot panel (SnapshotPanel) ────────────────────────────
  "app.snap.inspectingReadOnly": "Inspecting · read-only",
  "app.snap.close": "Close",
  "app.snap.readOnlyBannerStrong": "You are NOT signed in as this user.",
  "app.snap.readOnlyBannerRest": " Read-only data view; nothing you do here affects their session, billing, or workspace data — except where explicitly labelled (e.g. \"Grant free override\").",
  "app.snap.owner": "Owner",
  "app.snap.email": "Email",
  "app.snap.userCreated": "User created",
  "app.snap.lastSignIn": "Last sign-in",
  "app.snap.userId": "User ID",
  "app.snap.subscription": "Subscription",
  "app.snap.status": "Status",
  "app.snap.plan": "Plan",
  "app.snap.currency": "Currency",
  "app.snap.trialEnds": "Trial ends",
  "app.snap.currentPeriodEnds": "Current period ends",
  "app.snap.cancelAtPeriodEnd": "Cancel at period end",
  "app.snap.hasActiveAccess": "Has active access",
  "app.snap.archived": "Archived",
  "app.snap.yes": "Yes",
  "app.snap.no": "No",
  "app.snap.stripeCustomer": "Stripe customer",
  "app.snap.stripeSubscription": "Stripe subscription",
  "app.snap.freeOverride": "Free override",
  "app.snap.freeOverrideDesc": "Grants full access to one workspace without charge, with an optional time limit. When it expires (or is revoked) the user keeps their workspace and lands on the subscribe paywall. Every grant, extension and revoke is logged in the audit log with your identity and timestamp.",
  "app.snap.freeOverrideActive": "Free override is active",
  "app.snap.freeOverrideExpiredTitle": "Free override has EXPIRED",
  "app.snap.freeOverrideExpiresPrefix": "Expires:",
  "app.snap.freeOverrideExpiredPrefix": "Expired:",
  "app.snap.freeOverrideNoExpiry": "No expiry",
  "app.snap.freeOverrideExpiryLabel": "Time limit",
  "app.snap.expiry7": "7 days",
  "app.snap.expiry14": "14 days",
  "app.snap.expiry30": "30 days",
  "app.snap.expiryCustom": "Custom date…",
  "app.snap.expiryNone": "No expiry",
  "app.snap.extendFreeOverride": "Extend / change expiry",
  "app.snap.updateExpiry": "Update expiry",
  "app.snap.cancelBtn": "Cancel",
  "app.snap.expiryInvalid": "Pick a valid expiry date.",
  "app.snap.expiryMustBeFuture": "Expiry must be in the future.",
  "app.snap.reasonPrefix": "Reason:",
  "app.snap.working": "Working…",
  "app.snap.revokeFreeOverride": "Revoke free override",
  "app.snap.freeOverrideReasonPlaceholder": "Reason (e.g. early-access partner, refund-replacement)",
  "app.snap.grantFreeOverride": "Grant free override",
  "app.snap.workspaces": "Workspaces",
  "app.snap.newWorkspace": "+ New workspace",
  "app.snap.createWorkspaceOnBehalf": "Create workspace on behalf",
  "app.snap.wsNamePlaceholder": "Workspace name (e.g. Joe's Pizza)",
  "app.snap.marketUk": "United Kingdom (GBP)",
  "app.snap.marketUae": "United Arab Emirates (AED)",
  "app.snap.marketUs": "United States (USD)",
  "app.snap.marketIn": "India (INR)",
  "app.snap.cityPlaceholder": "City",
  "app.snap.areaPlaceholder": "Area (optional)",
  "app.snap.pricing": "Pricing",
  "app.snap.standardRate": "Standard rate",
  "app.snap.freeComp": "Free (comp)",
  "app.snap.customPrice": "Custom price",
  "app.snap.pricePlaceholder": "e.g. 4.99",
  "app.snap.perMonthSlash": "/month",
  "app.snap.newWsReasonPlaceholder": "Reason (e.g. partnership trial, comped support)",
  "app.snap.creating": "Creating…",
  "app.snap.createWorkspace": "Create workspace",
  "app.snap.noWorkspacesClickNew1": "No workspaces. Click ",
  "app.snap.noWorkspacesClickNew2": " above to create one for this account.",
  "app.snap.badgeArchived": "ARCHIVED",
  "app.snap.badgeCancelPending": "CANCEL PENDING",
  "app.snap.menuSuffix": "menu",
  "app.snap.logsSuffix": "logs",
  "app.snap.lastPrefix": "last",
  "app.snap.standardRateCard": "Standard rate card",
  "app.snap.badgeFree": "FREE",
  "app.snap.edit": "Edit",
  "app.snap.setOverride": "Set override",
  "app.snap.free": "Free",
  "app.snap.editReasonPlaceholder": "Reason (e.g. partnership deal, 6th workspace comp)",
  "app.snap.saving": "Saving…",
  "app.snap.savePricing": "Save pricing",
  "app.snap.overrideAuditNote": "Override is stored and audit-logged. Stripe billing will reflect it once the subscription-items logic is updated to read these values.",
  "app.snap.team": "Team",
  "app.snap.noTeamMembers": "No team members.",
  "app.snap.lastSignInPrefix": "Last sign-in",
  "app.snap.never": "never",
  "app.snap.loadingEllipsis": "Loading…",

  // ── Admin: Audit log tab (AuditTab) ──────────────────────────────────
  "app.audit.title": "Audit log",
  "app.audit.desc": "Every privileged action recorded with actor, target, IP and timestamp. Useful for compliance and tracking who did what.",
  "app.audit.refresh": "Refresh",
  "app.audit.allActions": "All actions",
  "app.audit.actionViewSnapshot": "Inspect (view_snapshot)",
  "app.audit.actionGrantOverride": "Grant free override",
  "app.audit.actionRevokeOverride": "Revoke free override",
  "app.audit.actionAddSuperAdmin": "Add super admin",
  "app.audit.actionRemoveSuperAdmin": "Remove super admin",
  "app.audit.errorPrefix": "Error:",
  "app.audit.loadingEllipsis": "Loading…",
  "app.audit.noEntries": "No audit entries.",
  "app.audit.noRowsMatch": "No rows on this page match the column filters.",
  "app.audit.colWhen": "When",
  "app.audit.colActor": "Actor",
  "app.audit.colAction": "Action",
  "app.audit.colTarget": "Target",
  "app.audit.colDetails": "Details",
  "app.audit.colIp": "IP",
  "app.audit.entriesTotalSuffix": "entries total",
  "app.audit.prev": "Prev",
  "app.audit.next": "Next",

  // ── Admin: Super admins tab (SuperAdminsTab) ─────────────────────────
  "app.sadmin.title": "Super admins",
  "app.sadmin.desc": "People with full access to this panel. Adding someone here gives them the ability to view all accounts, inspect, grant free overrides, and revoke other super admins.",
  "app.sadmin.addSuperAdmin": "Add a super admin",
  "app.sadmin.mustHaveAccount": "User must already have a Netrev account (signed up at least once). You cannot remove yourself.",
  "app.sadmin.notesPlaceholder": "Notes (optional)",
  "app.sadmin.working": "Working…",
  "app.sadmin.addSuperAdminBtn": "Add super admin",
  "app.sadmin.currentSuperAdmins": "Current super admins",
  "app.sadmin.errorPrefix": "Error:",
  "app.sadmin.colEmail": "Email",
  "app.sadmin.colLastSignIn": "Last sign-in",
  "app.sadmin.colAdded": "Added",
  "app.sadmin.colNotes": "Notes",
  "app.sadmin.loadingEllipsis": "Loading…",
  "app.sadmin.noSuperAdmins": "No super admins.",
  "app.sadmin.never": "Never",
  "app.sadmin.revoke": "Revoke",

  // ── Team page (TeamPage) ─────────────────────────────────────────────
  "app.team.title": "Team",
  "app.team.descOwner": "Invite operators to help run your restaurants. Operators can view data and add daily logs / price changes for the workspaces you assign them to. They can't see billing or change configuration.",
  "app.team.descOperator": "You're viewing the team for your account. Only the brand owner can invite or remove operators.",
  "app.team.refresh": "Refresh",
  "app.team.teamsYouBelongTo": "Teams you belong to",
  "app.team.colBrandAccount": "Brand account",
  "app.team.colYourRole": "Your role",
  "app.team.colInvitedBy": "Invited by",
  "app.team.colWorkspaces": "Workspaces",
  "app.team.badgeActive": "ACTIVE",
  "app.team.badgeArchived": "ARCHIVED",
  "app.team.all": "All",
  "app.team.switchBrandsNote": "Switch between brand accounts using the selector at the top of the sidebar.",
  "app.team.yourMembership": "Your team membership",
  "app.team.labelTeam": "TEAM",
  "app.team.labelInvitedBy": "INVITED BY",
  "app.team.labelYourRole": "YOUR ROLE",
  "app.team.roleOperator": "Operator",
  "app.team.labelWorkspacesAccess": "WORKSPACES YOU CAN ACCESS",
  "app.team.needAccessPrefix": "Need access to a different workspace? Ask ",
  "app.team.needAccessFallback": "your brand owner",
  "app.team.needAccessSuffix": " to update your access.",
  "app.team.inviteOperator": "Invite an operator",
  "app.team.inviteDesc": "The operator gets an email with a sign-in link. If they already have a Netrev account, they keep using it; if not, they create one. You can also copy the invite link directly to share.",
  "app.team.operatorEmailPlaceholder": "operator@example.com",
  "app.team.workspacesTheyAccess": "Workspaces they can access",
  "app.team.noActiveWorkspacesAssign": "No active workspaces to assign.",
  "app.team.sending": "Sending…",
  "app.team.sendInvite": "Send invite",
  "app.team.inviteCreatedFor": "✓ Invite created for",
  "app.team.emailSent": "An email has been sent with a sign-in link.",
  "app.team.emailNotSent": "Couldn't send the email automatically — share the link below directly.",
  "app.team.copied": "✓ Copied",
  "app.team.copyLink": "Copy link",
  "app.team.pendingInvites": "Pending invites",
  "app.team.colEmail": "Email",
  "app.team.colWorkspacesPlain": "Workspaces",
  "app.team.colExpires": "Expires",
  "app.team.colStatus": "Status",
  "app.team.badgeExpired": "EXPIRED",
  "app.team.badgePending": "PENDING",
  "app.team.resend": "Resend",
  "app.team.revoke": "Revoke",
  "app.team.members": "Members",
  "app.team.colRole": "Role",
  "app.team.colWorkspaceAccess": "Workspace access",
  "app.team.colJoined": "Joined",
  "app.team.colLastSignIn": "Last sign-in",
  "app.team.noTeamMembers": "No team members.",
  "app.team.roleBrandOwner": "Brand Owner",
  "app.team.allWorkspaces": "All workspaces",
  "app.team.noAccessSet": "No access set",
  "app.team.editAccess": "Edit access",
  "app.team.remove": "Remove",
  "app.team.editAccessForPrefix": "Edit workspace access for",
  "app.team.cancel": "Cancel",
  "app.team.saving": "Saving…",
  "app.team.save": "Save",

  // ── Accept invite page (AcceptInvitePage) ────────────────────────────
  "app.invite.teamInvite": "Team invite",
  "app.invite.loadingInvite": "Loading invite…",
  "app.invite.cantOpen": "Can't open this invite",
  "app.invite.welcomeToTeam": "Welcome to the team!",
  "app.invite.alreadyOnTeam": "You're already on this team",
  "app.invite.invitedToPrefix": "You've been invited to ",
  "app.invite.invitedToFallback": "an account",
  "app.invite.returnToNetrev": "Return to Netrev",
  "app.invite.alreadyMemberOf": "You're already a member of ",
  "app.invite.asBrandOwner": " as the brand owner",
  "app.invite.brandOwnerTestNote": "If you wanted to test an operator invite, sign out first and open this link in an incognito window with a different email.",
  "app.invite.notNeeded": "This invite isn't needed — you can already access this account.",
  "app.invite.goToDashboard": "Go to dashboard →",
  "app.invite.adminInvitedPrefix": "An admin (",
  "app.invite.adminInvitedMid": ") invited ",
  "app.invite.adminInvitedSuffix": " to join as an operator.",
  "app.invite.invitedAsOperator": "You've been invited as an operator on this account.",
  "app.invite.ableToViewData": "You'll be able to view data and update daily logs for the workspaces assigned to you.",
  "app.invite.joiningTeam": "Joining team…",
  "app.invite.acceptAndJoin": "Accept invite & join",
  "app.invite.signInWithEmailPrefix": "Sign in with the email this invite was sent to (",
  "app.invite.signInWithEmailSuffix": ") — or create an account if you don't have one yet.",
  "app.invite.signInToAccept": "Sign in to accept →",
  "app.invite.joinedPrefix": "✓ Joined ",
  "app.invite.joinedSuffix": ". Redirecting…",

  // ── Shared (Loading) ─────────────────────────────────────────────────
  "app.shared.loadingEllipsis": "Loading…",

  // ── Admin view chrome (AdminView) ────────────────────────────────────
  "app.view.brandSwitch": "Brand account · switch",
  "app.view.tapToSwitchBrands": "Tap to switch between your brands",
  "app.view.operatorSuffix": "(operator)",
  "app.view.archivedSuffix": "— archived",
  "app.view.workspace": "Workspace",
  "app.view.menu": "Menu",
  "app.view.createWorkspace": "Create workspace",
  "app.view.account": "Account",
  "app.view.currency": "Currency",
  "app.view.gbpLabel": "£ GBP",
  "app.view.workspaces": "Workspaces",
  "app.view.workspaceFallback": "Workspace",
  "app.view.cancelPending": "Cancel pending",
  "app.view.uaeBadge": "🇦🇪 UAE",
  "app.view.all": "All",
  "app.view.addMenuItem": "+ Add menu item",
  "app.view.exportCsv": "Export CSV",
  "app.view.exportCsvTitle": "Download the rows shown as a spreadsheet",
  "app.view.addLogEntry": "+ Add log entry",
  "app.view.addCpcSpend": "+ Add CPC spend",
  "app.view.addCoFund": "+ Add platform contribution",
  "app.view.itemsSuffix": "items",
  "app.view.recordsSuffix": "records",
  "app.view.noWorkspacesYet": "No workspaces yet — create one in Subscriptions",
  "app.view.cancellationPendingPrefix": "Cancellation pending for",
  "app.view.cancellationPendingBody1": "This workspace will be archived on ",
  "app.view.cancellationPendingFallbackDate": "your next billing date",
  "app.view.cancellationPendingBody2": ". You can still edit it until then, or undo the cancellation.",
  "app.view.manage": "Manage",
  "app.view.welcomeToNetrev": "WELCOME TO NETREV",
  "app.view.setUpFirstRestaurant": "Let's set up your first restaurant",
  "app.view.workspaceWord": "workspace",
  "app.view.welcomeBody1": "A ",
  "app.view.welcomeBody2": " is one of your restaurant locations. Here you will be able to track your daily profit P&L across all platforms — Deliveroo, Uber Eats, Talabat, Careem, and more.",
  "app.view.step1Title": "Create workspace",
  "app.view.step1Desc": "Pick your market (UK, UAE, US or India) and add your restaurant name",
  "app.view.step2Title": "Set your rates",
  "app.view.step2Desc": "Configure commission %, VAT, COGS for each delivery platform",
  "app.view.step3Title": "Track real profit",
  "app.view.step3Desc": "Log daily sales and see true margin after all fees",
  "app.view.noActiveWorkspaces": "No active workspaces",
  "app.view.noActiveWorkspacesBody": "You have archived workspaces below. Restore one or create a new workspace to continue.",
  "app.view.noWorkspaceSelected": "No workspace selected",
  "app.view.createWsToTrackPrefix": "Create a workspace from ",
  "app.view.subscriptionsWord": "Subscriptions",
  "app.view.createWsToTrackSuffix": " to start tracking profit",
  "app.view.createWsToTrackAdmin": ", or jump to the Admin section in the sidebar to manage other accounts.",
  "app.view.createWsToTrackPeriod": ".",
  "app.view.disclaimerLabel": "Disclaimer:",
  "app.view.disclaimerBody": " Netrev provides estimates for planning purposes only, calculated from the data and rates you enter. Figures are not guaranteed to be accurate and may differ from your official platform statements. This is not financial, accounting, tax, or legal advice — always verify against your aggregator statements and consult a qualified professional before making business decisions.",

  // ── Help widget (HelpWidget) ─────────────────────────────────────────
  "app.help.needHelp": "Need help?",
  "app.help.sendMessageReply": "Send us a message and we'll reply by email.",
  "app.help.thanksReplyPrefix": "Thanks — we'll get back to you at",
  "app.help.subject": "Subject",
  "app.help.subjectPlaceholder": "What do you need help with?",
  "app.help.message": "Message",
  "app.help.messagePlaceholder": "Tell us what's going on (at least 10 characters)",
  "app.help.removeTooltip": "Remove",
  "app.help.attachScreenshots": "Attach screenshots",
  "app.help.sending": "Sending…",
  "app.help.send": "Send",
  "app.help.helpSupportTooltip": "Help & support",

  // ── Main API errors (lib/apiError.js) ────────────────────────────────
  // The .NET platform's error codes on /api/support and /api/anthropic (the
  // help widget and AI insight). Express's own sentences are shown as sent.
  "app.apiErr.bad_request": "Netrev couldn't read that request. Please try again.",
  "app.apiErr.forbidden": "You don't have permission to do this.",
  "app.apiErr.not_found": "Netrev couldn't find what this screen asked for. Please refresh the page and try again.",
  "app.apiErr.method_not_allowed": "Netrev couldn't handle that request. Please refresh the page and try again.",
  "app.apiErr.payload_too_large": "That's too much to send at once. Please send less and try again.",
  "app.apiErr.unsupported_media_type": "Netrev couldn't read that request. Please refresh the page and try again.",
  "app.apiErr.rate_limited": "Too many requests. Please wait a moment and try again.",
  "app.apiErr.internal_error": "Something went wrong on our side. Please try again in a moment.",
  "app.apiErr.db_unavailable": "Netrev can't reach its database right now. Please try again shortly.",
  "app.apiErr.upstream_unavailable": "A service Netrev relies on can't be reached right now. Please try again shortly.",
  "app.apiErr.auth_unavailable": "The sign in service can't be reached right now. Please try again shortly.",
  "app.apiErr.service_unavailable": "Netrev is unavailable right now. Please try again shortly.",
  "app.apiErr.timeout": "Netrev took too long to answer. Please try again.",
  "app.apiErr.unknown": "Something went wrong. Please try again.",
  "app.apiErr.network": "Couldn't reach Netrev. Check your connection and try again.",

  // ── Recover password (RecoverPassword) ───────────────────────────────
  "app.recover.passwordUpdated": "Password updated",
  "app.recover.redirectingSignIn": "Redirecting you to sign in…",
  "app.recover.setNewPassword": "Set a new password",
  "app.recover.chooseStrongPassword": "Choose a strong password you haven't used before. Minimum 8 characters.",
  "app.recover.newPassword": "New password",
  "app.recover.atLeast8": "At least 8 characters",
  "app.recover.confirmNewPassword": "Confirm new password",
  "app.recover.repeatPassword": "Repeat the password",
  "app.recover.updating": "Updating…",
  "app.recover.updatePassword": "Update password",

  // ── Trial banner / subscription status ───────────────────────────────
  "app.trial.endsInPrefix": "Trial ends in",
  "app.trial.freeAccessEndsInPrefix": "Free access ends in",
  "app.trial.subscribeNow": "Subscribe now →",
  "app.trial.noCardHint": "no card needed yet",
  "app.trial.noCardUrgentHint": "add a card to keep access",
  "app.trial.addCard": "Add a card →",
  "app.lock.subscribeToUnlock": "Subscribe to unlock",
  "app.lock.priceToolTitle": "Price Tool is part of the paid plan",
  "app.lock.priceToolBody": "Model discounts, campaign fees and item-level margins once you subscribe. Your Daily Log and P&L keep working during the trial.",
  "app.lock.paidOnlyNav": "Paid plan",
  "app.admin.funnelTitle": "Signup funnel · last 30 days",
  "app.admin.funnelUnavailable": "Funnel not available yet — run migration 042.",
  "app.admin.funnel.signedUp": "Signed up",
  "app.admin.funnel.confirmed": "Confirmed email",
  "app.admin.funnel.signedIn": "Signed in",
  "app.admin.funnel.firstWorkspace": "Created a workspace",
  "app.admin.funnel.firstLog": "Logged 1+ day",
  "app.admin.funnel.sevenLogs": "Logged 7+ days",
  "app.admin.funnel.cardAdded": "Card on file",
  "app.admin.funnel.paid": "Paying",
  "app.admin.funnel.eventsPrefix": "In-app moments:",
  "app.admin.funnel.openedCheckout": "opened checkout",
  "app.admin.funnel.sawPaywall": "hit the paywall",
  "app.admin.funnel.uploadedStatement": "uploaded a statement",
  "app.admin.funnel.eventsPending": "event tracking starts once migration 042 is applied",
  "app.admin.funnel.col.email": "Email",
  "app.admin.funnel.col.region": "Region",
  "app.admin.funnel.col.signedUp": "Signed up",
  "app.admin.funnel.col.stage": "Reached",
  "app.admin.funnel.col.lastSeen": "Last seen",
  "app.admin.funnel.col.logs": "Logs",
  "app.umodal.addCardKeepTrial": "Add a card · keep my free trial →",
  "app.umodal.noChargeUntilPrefix": "Nothing is charged today. Your first payment is on ",
  "app.umodal.noChargeUntilSuffix": ", when the trial ends — cancel any time before then.",
  "app.umodal.payNowUnlock": "Pay now instead · unlock all platforms today",
  "app.umodal.payNowAlsoLiftsLimit": "Paying now also lifts the trial's one-site limit and unlocks the Price Tool and AI insights. Adding a card keeps them until your trial ends.",
  "app.status.free": "Free",
  "app.status.annual": "Annual",
  "app.status.monthly": "Monthly",
  "app.status.trialStartsOnFirstWs": "Trial starts on first workspace",
  "app.status.trialPrefix": "Trial:",
  "app.status.paymentFailed": "Payment Failed",

  // ── Upgrade modal (UpgradeModal) ─────────────────────────────────────
  "app.umodal.subscribeToNetrev": "Subscribe to Netrev",
  "app.umodal.pickYourPlan": "Pick your plan",
  "app.umodal.siteSingular": "site",
  "app.umodal.sitePlural": "sites",
  "app.umodal.billedIn": "Billed in",
  "app.umodal.monthly": "Monthly",
  "app.umodal.perMonth": "per month",
  "app.umodal.cancelAnytime": "Cancel anytime",
  "app.umodal.annual": "Annual",
  "app.umodal.perYear": "per year",
  "app.umodal.twelveMonthCommitment": "12-month commitment",
  "app.umodal.cardOnFileStrong": "Pay now",
  "app.umodal.cardOnFilePrefix": "Card already on file. ",
  "app.umodal.cardOnFileSuffix": " to end your trial and unlock all features immediately. Otherwise you'll be auto-billed when trial ends.",
  "app.umodal.unlockedRefreshing": "✓ Unlocked — refreshing your workspace…",
  "app.umodal.activating": "Activating your subscription…",
  "app.umodal.redirectingStripe": "Redirecting to Stripe…",
  "app.umodal.payNowEndTrial": "Pay now · end trial & unlock all features →",
  "app.umodal.usingCardOnFile": "Using the card you already have on file. No redirect needed.",
  "app.umodal.redirectSecureSsl": "You'll be redirected to Stripe. Secure payment via 256-bit SSL.",
  "app.umodal.continueToStripe": "Continue to Stripe →",

  // ── Trial end paywall (TrialEndPaywall) ──────────────────────────────
  "app.paywallT.trialEnded": "Trial ended",
  "app.paywallT.chooseWorkspaces": "Choose your workspaces",
  "app.paywallT.youCreatedPrefix": "You created ",
  "app.paywallT.workspaceSingular": "workspace",
  "app.paywallT.workspacePlural": "workspaces",
  "app.paywallT.duringTrialBody": " during your trial. Pick the ones to keep — the rest will be archived (you can restore them within 90 days by resubscribing).",
  "app.paywallT.freeAccessEnded": "Free access ended",
  "app.paywallT.duringOverrideBody": " during your free access period. Pick the ones to keep — the rest will be archived (you can restore them within 90 days by resubscribing).",
  "app.paywallT.noWorkspacesToDisplay": "No workspaces to display.",
  "app.paywallT.untitled": "Untitled",
  "app.paywallT.willBeArchived": "· Will be archived",
  "app.paywallT.monthly": "Monthly",
  "app.paywallT.annual": "Annual",
  "app.paywallT.keepingPrefix": "Keeping",
  "app.paywallT.willBeArchivedRetention": "will be archived (90-day retention)",
  "app.paywallT.total": "Total",
  "app.paywallT.redirectingStripe": "Redirecting to Stripe…",
  "app.paywallT.selectAtLeastOne": "Select at least one workspace",
  "app.paywallT.continueToStripe": "Continue to Stripe →",
  "app.paywallT.cancelTitle": "Cancel your account?",
  "app.paywallT.cancelBody": "All your workspaces will be archived. You'll be signed out. Your data will be preserved for 90 days — you can resubscribe within that time to restore everything.",
  "app.paywallT.cancelConfirmLabel": "Yes, cancel everything",
  "app.paywallT.cancelGoBack": "Go back",
  "app.paywallT.cancelEverything": "Cancel everything",
  "app.paywallT.signOut": "Sign out",
  "app.paywallT.securePaymentNote": "Secure payment via Stripe. You can cancel or change your subscription anytime.",

  // ── Paywall screen (PaywallScreen) ───────────────────────────────────
  "app.paywall.subscribeNow": "Subscribe now",
  "app.paywall.trialHasEnded": "Your trial has ended",
  "app.paywall.trialHasEndedBody": "Subscribe to continue accessing your data, daily logs, and AI insights. Your existing data is safe and waiting for you.",
  "app.paywall.paymentFailed": "Payment failed",
  "app.paywall.paymentFailedBody": "Your last payment couldn't be processed — usually an expired card or insufficient funds. Update your payment method to restore access. Your workspace and all your data are preserved.",
  "app.paywall.updatePaymentMethod": "Update payment method",
  "app.paywall.paymentNeedsVerification": "Payment needs a one-time verification",
  "app.paywall.paymentNeedsVerificationBodyINR": "Your last renewal couldn't be collected automatically — cards issued in India usually need a one-time OTP verification before recurring payments can be set up. Complete the payment on the secure page to keep your subscription active; after that, renewals are collected automatically. Your workspace and all your data are preserved.",
  "app.paywall.completeVerification": "Complete verification",
  "app.paywall.accountArchived": "Account archived",
  "app.paywall.archivedBody1": "Your subscription was canceled. We're holding your data for ",
  "app.paywall.archivedBody2": " more day",
  "app.paywall.archivedBody2Plural": " more days",
  "app.paywall.archivedBodyDays": ". Resubscribe to restore access immediately.",
  "app.paywall.resubscribe": "Resubscribe",
  "app.paywall.dataRetentionExpired": "Data retention expired",
  "app.paywall.dataRetentionExpiredBody": "Your 90-day retention window has passed. Subscribe to start fresh, or contact support if you need previous data.",
  "app.paywall.subscriptionRequired": "Subscription required",
  "app.paywall.subscriptionRequiredBody": "Subscribe to access your Netrev workspace.",
  "app.paywall.reactivateTitle": "Reactivate your subscription?",
  "app.paywall.subscribingWill": "Subscribing again will:",
  "app.paywall.reactBullet1Strong": "Unlock full features immediately",
  "app.paywall.reactBullet1Rest": " — daily logs, AI insights, P&L tracking",
  "app.paywall.reactBullet2Strong": "Start billing today",
  "app.paywall.reactBullet2Rest": " — your card on file will be charged for the first period",
  "app.paywall.reactBullet3Strong": "Restore your archived data",
  "app.paywall.reactBullet3Rest": " — workspaces, channels, and logs come back online",
  "app.paywall.cancelAnytimeSettings": "You can cancel anytime from billing settings. ",
  "app.paywall.wellTakeYouToStripe": "We'll take you to Stripe to confirm.",
  "app.paywall.reactConfirmLabel": "Yes, reactivate",
  "app.paywall.yourAccount": "Your account",
  "app.paywall.openingStripe": "Opening Stripe…",
  "app.paywall.signOut": "Sign out",
  "app.paywall.needHelpEmail": "Need help? Email ",

  // ── Manage billing button (ManageBillingButton) ──────────────────────
  "app.billing.opening": "Opening…",
  "app.billing.manageBilling": "Manage billing",

  // ── Top nav plan pills (App root) ────────────────────────────────────
  "app.root.superAdmin": "Super admin",
  "app.root.free": "Free",
  "app.root.annual": "Annual",
  "app.root.monthly": "Monthly",
  "app.root.trial": "Trial",
  "app.root.pastDue": "Past due",
  "app.root.canceled": "Canceled",
};

export const ar = {
  // ── Mobile: workspace picker ─────────────────────────────────────────
  "app.mob.switchWorkspace": "تبديل مساحة العمل",

  // ── Mobile: dashboard ────────────────────────────────────────────────
  "app.mob.performance": "الأداء",
  "app.mob.grossRevenue": "إجمالي الإيرادات",
  "app.mob.netReceivable": "صافي المستحق",
  "app.mob.vatPaid": "ضريبة القيمة المضافة المدفوعة",
  "app.mob.trueProfit": "الربح الحقيقي",
  "app.mob.avgMonthlyProfit": "متوسط الربح الشهري",
  "app.mob.trueProfitPerMonth": "الربح الحقيقي / الشهر",
  "app.mob.recentMonths": "الأشهر الأخيرة",
  "app.mob.grossSuffix": "إجمالي",

  // ── Mobile: items ────────────────────────────────────────────────────
  "app.mob.add": "+ إضافة",
  "app.mob.newItem": "صنف جديد",
  "app.mob.itemName": "اسم الصنف",
  "app.mob.itemNamePlaceholder": "مثال: برجر دجاج",
  "app.mob.yourCost": "تكلفتك",
  "app.mob.listingPrice": "سعر العرض",
  "app.mob.discountTier": "فئة الخصم",
  "app.mob.none": "بدون",
  "app.mob.campaignFee": "رسوم الحملة",
  "app.mob.campaignFeeHint": "تُملأ تلقائيًا من الفئة · يمكن تجاوزها عند الحاجة",
  "app.mob.remove": "إزالة",
  "app.mob.cost": "التكلفة",
  "app.mob.fullBreakdown": "التفصيل الكامل",
  "app.mob.allFormulasVerified": "جميع المعادلات مُتحقَّق منها ✅",
  "app.mob.sellingPrice": "سعر البيع",
  "app.mob.discount": "الخصم",
  "app.mob.campaignDeliveryFee": "رسوم الحملة / التوصيل",
  "app.mob.netTicketTotal": "إجمالي صافي الفاتورة",
  "app.mob.aggregatorAt": "المنصة @",
  "app.mob.netReceivableFromAgg": "صافي المستحق من المنصة",
  "app.mob.vatAt": "ضريبة القيمة المضافة @",
  "app.mob.profitPerSale": "✓ ربح لكل عملية بيع",
  "app.mob.lossPerSale": "✗ خسارة لكل عملية بيع",
  "app.mob.afterCostPrefix": "بعد التكلفة (£",
  "app.mob.afterCostSuffix": ")، الضريبة وجميع الاستقطاعات",
  "app.mob.allItems": "جميع الأصناف",
  "app.mob.colItem": "الصنف",
  "app.mob.colCost": "التكلفة",
  "app.mob.colPrice": "السعر",
  "app.mob.colDisc": "الخصم",
  "app.mob.colFee": "الرسوم",
  "app.mob.colRecv": "المستحق",
  "app.mob.colPL": "ربح/خسارة",

  // ── Mobile: log ──────────────────────────────────────────────────────
  "app.mob.logSuffix": "السجل",
  "app.mob.seeAdminFullHistory": "راجع الإدارة لعرض سجل 5 سنوات كاملًا",
  "app.mob.logTodaysSales": "+ تسجيل مبيعات اليوم",
  "app.mob.newEntry": "إدخال جديد",
  "app.mob.date": "التاريخ",
  "app.mob.gross": "الإجمالي",
  "app.mob.netOnTicket": "صافي الفاتورة",
  "app.mob.cpcSpend": "إنفاق CPC",
  "app.mob.netAfterDiscComm": "الصافي بعد الخصم والعمولة",
  "app.mob.cardFees": "رسوم البطاقة",
  "app.mob.vat": "ضريبة القيمة المضافة",
  "app.mob.earningAfterVat": "الأرباح بعد ضريبة القيمة المضافة",
  "app.mob.cogs": "تكلفة البضاعة المباعة",
  "app.mob.cogsGrossSuffix": "من الإجمالي",
  "app.mob.pctEarningOnGross": "% الأرباح على الإجمالي",
  "app.mob.netProfitPct": "% صافي الربح",
  "app.mob.cogsPctOnNet": "% تكلفة البضاعة على الصافي",

  // ── Mobile: account ──────────────────────────────────────────────────
  "app.mob.totalMonthlyBilling": "إجمالي الفوترة الشهرية",
  "app.mob.perMonth": "/شهر",
  "app.mob.monthlyCharge": "الرسوم الشهرية",
  "app.mob.nextBilling": "الفوترة التالية",
  "app.mob.manageBilling": "إدارة الفوترة",
  "app.mob.addAnotherLocation": "إضافة موقع آخر؟",
  "app.mob.ownMenuAgg": "/شهر · قائمة خاصة · منصة خاصة",
  "app.mob.addWorkspace": "+ إضافة مساحة عمل",
  "app.mob.newWorkspace": "مساحة عمل جديدة",
  "app.mob.locationName": "اسم الموقع",
  "app.mob.locationNamePlaceholder": "مثال: Burger Bite — ليدز",
  "app.mob.aggregatorPlatform": "منصة التجميع",
  "app.mob.confirmSubscription": "تأكيد الاشتراك",
  "app.mob.platform": "المنصة",
  "app.mob.addedCharge": "الرسوم المضافة",
  "app.mob.newTotal": "الإجمالي الجديد",
  "app.mob.redirectingStripe": "🔒 جارٍ التحويل إلى Stripe لتفويض هذا الاشتراك.",
  "app.mob.back": "← رجوع",
  "app.mob.since": "منذ",
  "app.mob.itemsSuffix": "صنف",
  "app.mob.cancel": "إلغاء",

  // ── Auth panel ───────────────────────────────────────────────────────
  "app.auth.realProfitHeadline": "الربح الحقيقي على كل طلب.",
  "app.auth.realProfitSub": "تتبّع الأرباح والخسائر عبر كل منصة توصيل. اطّلع على ما يتبقى فعليًا بعد العمولات ورسوم البطاقة وضريبة القيمة المضافة وتكلفة الطعام — يوميًا، لكل منصة.",
  "app.auth.commissionDeducted": "العمولة المستقطعة",
  "app.auth.vatCalculated": "ضريبة القيمة المضافة المحتسبة",
  "app.auth.trueProfitMargin": "هامش الربح الحقيقي",
  "app.auth.supports": "يدعم",

  // ── Auth screen ──────────────────────────────────────────────────────
  "app.auth.unitedKingdom": "المملكة المتحدة",
  "app.auth.billedInGbp": "الفوترة بالجنيه الإسترليني",
  "app.auth.uae": "الإمارات",
  "app.auth.billedInAed": "الفوترة بالدرهم الإماراتي",
  "app.auth.unitedStates": "الولايات المتحدة",
  "app.auth.billedInUsd": "الفوترة بالدولار الأمريكي",
  "app.auth.india": "الهند",
  "app.auth.billedInInr": "الفوترة بالروبية الهندية",
  "app.view.navigation": "التنقل",
  "app.auth.regionLockNote": "يحدد هذا عملة الفوترة لحسابك. ستنشئ مساحات عمل لهذه المنطقة فقط.",
  "app.auth.bizRestaurant": "مطعم",
  "app.auth.bizCloudKitchen": "مطبخ سحابي",
  "app.auth.bizPharmacy": "صيدلية",
  "app.auth.bizSupermarket": "سوبر ماركت",
  "app.auth.bizRetail": "تجارة إلكترونية",
  "app.auth.emailPlaceholder": "أدخل بريدك الإلكتروني",
  "app.auth.namePlaceholder": "أدخل اسمك",
  "app.auth.phonePlaceholder": "رقمك",
  "app.auth.passwordPlaceholderSignup": "أنشئ كلمة مرور (8 أحرف على الأقل)",
  "app.auth.passwordPlaceholderLogin": "أدخل كلمة المرور",
  "app.auth.signInArrow": "تسجيل الدخول →",
  "app.auth.weSentConfirmation": "لقد أرسلنا رابط تأكيد إلى",
  "app.auth.activateLinkExpires": "انقر الرابط لتفعيل حسابك. تنتهي صلاحية الرابط خلال 24 ساعة. تحقّق من مجلد البريد العشوائي إذا لم تجده.",
  "app.auth.useDifferentEmail": "استخدام بريد إلكتروني مختلف",
  "app.auth.signIn": "تسجيل الدخول",
  "app.auth.ifAccountExistsFor": "إذا كان هناك حساب لـ",
  "app.auth.weSentResetLink": "فقد أرسلنا رابط إعادة تعيين كلمة المرور.",
  "app.auth.resetLinkExpires": "انقر الرابط في البريد الإلكتروني لتعيين كلمة مرور جديدة. تنتهي صلاحية الرابط خلال ساعة واحدة. تحقّق من مجلد البريد العشوائي إذا لم تجده خلال دقيقة.",
  "app.auth.tryDifferentEmail": "جرّب بريدًا إلكترونيًا مختلفًا",
  "app.auth.backToSignIn": "العودة إلى تسجيل الدخول",
  "app.auth.byCreatingAgree": "بإنشاء حساب، فإنك توافق على ",
  "app.auth.terms": "الشروط",
  "app.auth.and": " و ",
  "app.auth.privacyPolicy": "سياسة الخصوصية",

  // ── Upgrade prompt ───────────────────────────────────────────────────
  "app.upgrade.proFeature": "ميزة Pro",
  "app.upgrade.featureAvailableSuffix": " متاح ضمن خطة Pro. قم بالترقية لفتح مساحات عمل غير محدودة، وسجل يومي كامل، واستيراد CSV، والمزيد.",
  "app.upgrade.upgradeProCta": "الترقية إلى Pro — ‎£49.99‎/شهر",
  "app.upgrade.cancelAnytimeNote": "إلغاء في أي وقت · لكل مساحة عمل · بدون التزام",

  // ── Admin: Overview tab ──────────────────────────────────────────────
  "app.admin.overviewTitle": "نظرة عامة للإدارة",
  "app.admin.overviewDesc": "مقاييس رئيسية عبر جميع حسابات Netrev. استخدم قائمة الإلغاءات في الأسفل للتواصل مباشرةً مع العملاء المنسحبين والمعرّضين للمخاطر.",
  "app.admin.refresh": "تحديث",
  "app.admin.totalAccounts": "إجمالي الحسابات",
  "app.admin.archivedSuffix": "مؤرشف",
  "app.admin.activeAccess": "وصول نشط",
  "app.admin.trialPaidComped": "تجريبي أو مدفوع أو مجاني",
  "app.admin.cancelled": "ملغى",
  "app.admin.churnRateSuffix": "معدل الانسحاب",
  "app.admin.estMrr": "الإيراد الشهري المتكرر التقديري",
  "app.admin.mrrApproxNote": "تقريبي · Stripe هو المصدر الموثوق",
  "app.admin.subscriptionMix": "توزيع الاشتراكات",
  "app.admin.noAccountsYet": "لا توجد حسابات بعد.",
  "app.admin.cancellationsAtRisk": "الإلغاءات والحسابات المعرّضة للمخاطر",
  "app.admin.churnCancelled": "ملغى",
  "app.admin.churnCancelling": "قيد الإلغاء",
  "app.admin.churnPastDue": "متأخر السداد",
  "app.admin.contactOpensEmail": "يفتح \"تواصل\" بريدًا إلكترونيًا جاهزًا لطلب الملاحظات في تطبيق بريدك.",
  "app.admin.noCancellationsYet": "لا توجد إلغاءات بعد",
  "app.admin.nothingToReachOut": "لا يوجد من نتواصل معه. مشكلة جيدة أن تكون لديك.",
  "app.admin.chipCancelled": "ملغى",
  "app.admin.chipCancelling": "قيد الإلغاء",
  "app.admin.chipPastDue": "متأخر السداد",
  "app.admin.colAccount": "الحساب",
  "app.admin.colOwnerEmail": "بريد المالك",
  "app.admin.colStatus": "الحالة",
  "app.admin.colPlan": "الخطة",
  "app.admin.colDaysActive": "أيام النشاط",
  "app.admin.colCancelledApprox": "تاريخ الإلغاء التقريبي",
  "app.admin.colPeriodEnds": "انتهاء الفترة",
  "app.admin.contact": "تواصل",

  // ── Admin: Accounts tab ──────────────────────────────────────────────
  "app.admin.accountsTitle": "الحسابات",
  "app.admin.accountsDesc": "كل حساب في Netrev، مع حالة الاشتراك والإيراد الشهري المتكرر ومساحات العمل. انقر فحص لرؤية اللقطة الكاملة للقراءة فقط لحساب ما.",
  "app.admin.colOwner": "المالك",
  "app.admin.colPhone": "رقم الهاتف",
  "app.admin.colSiteNames": "أسماء المواقع",
  "app.admin.colSites": "المواقع",
  "app.admin.colMrr": "الإيراد الشهري المتكرر",
  "app.admin.colPeriodEndShort": "انتهاء الفترة",
  "app.admin.colJoined": "انضم",
  "app.admin.searchEmailOrAccount": "ابحث بالبريد الإلكتروني أو اسم الحساب",
  "app.admin.allStatuses": "جميع الحالات",
  "app.admin.statusTrialing": "تجريبي",
  "app.admin.statusActive": "نشط",
  "app.admin.statusPastDue": "متأخر السداد",
  "app.admin.statusCancelled": "ملغى",
  "app.admin.statusIncomplete": "غير مكتمل",
  "app.admin.statusFreeOverride": "تجاوز مجاني",
  "app.admin.activeOnly": "النشطة فقط",
  "app.admin.archivedOnly": "المؤرشفة فقط",
  "app.admin.allAccounts": "جميع الحسابات",
  "app.admin.newestFirst": "الأحدث أولًا",
  "app.admin.oldestFirst": "الأقدم أولًا",
  "app.admin.emailAZ": "البريد الإلكتروني أ–ي",
  "app.admin.errorPrefix": "خطأ:",
  "app.admin.loadingEllipsis": "جارٍ التحميل…",
  "app.admin.noAccountsMatchFilters": "لا توجد حسابات تطابق عوامل التصفية الحالية.",
  "app.admin.noAccountsMatchColumn": "لا توجد حسابات في هذه الصفحة تطابق عوامل تصفية الأعمدة.",
  "app.admin.badgeFree": "مجاني",
  "app.admin.badgeFreeExpired": "مجاني · منتهٍ",
  "app.admin.badgeArchived": "مؤرشف",
  "app.admin.inspect": "فحص",
  "app.admin.totalSuffix": "الإجمالي ·",
  "app.admin.showing": "عرض",
  "app.admin.prev": "السابق",
  "app.admin.next": "التالي",

  // ── Admin: Snapshot panel ────────────────────────────────────────────
  "app.snap.inspectingReadOnly": "قيد الفحص · للقراءة فقط",
  "app.snap.close": "إغلاق",
  "app.snap.readOnlyBannerStrong": "أنت لست مسجّلًا الدخول بهوية هذا المستخدم.",
  "app.snap.readOnlyBannerRest": " عرض بيانات للقراءة فقط؛ لا يؤثر أي شيء تفعله هنا على جلسته أو فوترته أو بيانات مساحة عمله — باستثناء ما هو موضّح صراحةً (مثل \"منح تجاوز مجاني\").",
  "app.snap.owner": "المالك",
  "app.snap.email": "البريد الإلكتروني",
  "app.snap.userCreated": "تاريخ إنشاء المستخدم",
  "app.snap.lastSignIn": "آخر تسجيل دخول",
  "app.snap.userId": "معرّف المستخدم",
  "app.snap.subscription": "الاشتراك",
  "app.snap.status": "الحالة",
  "app.snap.plan": "الخطة",
  "app.snap.currency": "العملة",
  "app.snap.trialEnds": "انتهاء التجربة",
  "app.snap.currentPeriodEnds": "انتهاء الفترة الحالية",
  "app.snap.cancelAtPeriodEnd": "الإلغاء عند نهاية الفترة",
  "app.snap.hasActiveAccess": "لديه وصول نشط",
  "app.snap.archived": "مؤرشف",
  "app.snap.yes": "نعم",
  "app.snap.no": "لا",
  "app.snap.stripeCustomer": "عميل Stripe",
  "app.snap.stripeSubscription": "اشتراك Stripe",
  "app.snap.freeOverride": "تجاوز مجاني",
  "app.snap.freeOverrideDesc": "يمنح وصولًا كاملًا إلى مساحة عمل واحدة دون رسوم، مع مدة زمنية اختيارية. عند انتهاء المدة (أو الإلغاء) يحتفظ المستخدم بمساحة عمله ويظهر له جدار الاشتراك. يُسجَّل كل منح وتمديد وإلغاء في سجل التدقيق مع هويتك والطابع الزمني.",
  "app.snap.freeOverrideActive": "التجاوز المجاني نشط",
  "app.snap.freeOverrideExpiredTitle": "انتهت صلاحية التجاوز المجاني",
  "app.snap.freeOverrideExpiresPrefix": "ينتهي في:",
  "app.snap.freeOverrideExpiredPrefix": "انتهى في:",
  "app.snap.freeOverrideNoExpiry": "بدون تاريخ انتهاء",
  "app.snap.freeOverrideExpiryLabel": "المدة الزمنية",
  "app.snap.expiry7": "7 أيام",
  "app.snap.expiry14": "14 يومًا",
  "app.snap.expiry30": "30 يومًا",
  "app.snap.expiryCustom": "تاريخ مخصص…",
  "app.snap.expiryNone": "بدون تاريخ انتهاء",
  "app.snap.extendFreeOverride": "تمديد / تغيير تاريخ الانتهاء",
  "app.snap.updateExpiry": "تحديث تاريخ الانتهاء",
  "app.snap.cancelBtn": "إلغاء",
  "app.snap.expiryInvalid": "اختر تاريخ انتهاء صالحًا.",
  "app.snap.expiryMustBeFuture": "يجب أن يكون تاريخ الانتهاء في المستقبل.",
  "app.snap.reasonPrefix": "السبب:",
  "app.snap.working": "جارٍ التنفيذ…",
  "app.snap.revokeFreeOverride": "إلغاء التجاوز المجاني",
  "app.snap.freeOverrideReasonPlaceholder": "السبب (مثال: شريك وصول مبكر، استبدال استرداد)",
  "app.snap.grantFreeOverride": "منح تجاوز مجاني",
  "app.snap.workspaces": "مساحات العمل",
  "app.snap.newWorkspace": "+ مساحة عمل جديدة",
  "app.snap.createWorkspaceOnBehalf": "إنشاء مساحة عمل بالنيابة",
  "app.snap.wsNamePlaceholder": "اسم مساحة العمل (مثال: Joe's Pizza)",
  "app.snap.marketUk": "المملكة المتحدة (GBP)",
  "app.snap.marketUae": "الإمارات العربية المتحدة (AED)",
  "app.snap.marketUs": "الولايات المتحدة (USD)",
  "app.snap.marketIn": "الهند (INR)",
  "app.snap.cityPlaceholder": "المدينة",
  "app.snap.areaPlaceholder": "المنطقة (اختياري)",
  "app.snap.pricing": "التسعير",
  "app.snap.standardRate": "السعر القياسي",
  "app.snap.freeComp": "مجاني (هدية)",
  "app.snap.customPrice": "سعر مخصص",
  "app.snap.pricePlaceholder": "مثال: 4.99",
  "app.snap.perMonthSlash": "/شهر",
  "app.snap.newWsReasonPlaceholder": "السبب (مثال: تجربة شراكة، دعم مجاني)",
  "app.snap.creating": "جارٍ الإنشاء…",
  "app.snap.createWorkspace": "إنشاء مساحة عمل",
  "app.snap.noWorkspacesClickNew1": "لا توجد مساحات عمل. انقر ",
  "app.snap.noWorkspacesClickNew2": " في الأعلى لإنشاء واحدة لهذا الحساب.",
  "app.snap.badgeArchived": "مؤرشف",
  "app.snap.badgeCancelPending": "إلغاء معلّق",
  "app.snap.menuSuffix": "قائمة",
  "app.snap.logsSuffix": "سجلات",
  "app.snap.lastPrefix": "آخر",
  "app.snap.standardRateCard": "بطاقة السعر القياسي",
  "app.snap.badgeFree": "مجاني",
  "app.snap.edit": "تعديل",
  "app.snap.setOverride": "تعيين تجاوز",
  "app.snap.free": "مجاني",
  "app.snap.editReasonPlaceholder": "السبب (مثال: صفقة شراكة، مساحة العمل السادسة مجانًا)",
  "app.snap.saving": "جارٍ الحفظ…",
  "app.snap.savePricing": "حفظ التسعير",
  "app.snap.overrideAuditNote": "يُخزَّن التجاوز ويُسجَّل في التدقيق. ستعكسه فوترة Stripe بمجرد تحديث منطق عناصر الاشتراك لقراءة هذه القيم.",
  "app.snap.team": "الفريق",
  "app.snap.noTeamMembers": "لا يوجد أعضاء فريق.",
  "app.snap.lastSignInPrefix": "آخر تسجيل دخول",
  "app.snap.never": "أبدًا",
  "app.snap.loadingEllipsis": "جارٍ التحميل…",

  // ── Admin: Audit log tab ─────────────────────────────────────────────
  "app.audit.title": "سجل التدقيق",
  "app.audit.desc": "كل إجراء مميّز مُسجَّل مع المنفّذ والهدف وعنوان IP والطابع الزمني. مفيد للامتثال وتتبّع من فعل ماذا.",
  "app.audit.refresh": "تحديث",
  "app.audit.allActions": "جميع الإجراءات",
  "app.audit.actionViewSnapshot": "فحص (view_snapshot)",
  "app.audit.actionGrantOverride": "منح تجاوز مجاني",
  "app.audit.actionRevokeOverride": "إلغاء التجاوز المجاني",
  "app.audit.actionAddSuperAdmin": "إضافة مسؤول كبير",
  "app.audit.actionRemoveSuperAdmin": "إزالة مسؤول كبير",
  "app.audit.errorPrefix": "خطأ:",
  "app.audit.loadingEllipsis": "جارٍ التحميل…",
  "app.audit.noEntries": "لا توجد إدخالات تدقيق.",
  "app.audit.noRowsMatch": "لا توجد صفوف في هذه الصفحة تطابق عوامل تصفية الأعمدة.",
  "app.audit.colWhen": "متى",
  "app.audit.colActor": "المنفّذ",
  "app.audit.colAction": "الإجراء",
  "app.audit.colTarget": "الهدف",
  "app.audit.colDetails": "التفاصيل",
  "app.audit.colIp": "عنوان IP",
  "app.audit.entriesTotalSuffix": "إجمالي الإدخالات",
  "app.audit.prev": "السابق",
  "app.audit.next": "التالي",

  // ── Admin: Super admins tab ──────────────────────────────────────────
  "app.sadmin.title": "كبار المسؤولين",
  "app.sadmin.desc": "الأشخاص الذين لديهم وصول كامل إلى هذه اللوحة. إضافة شخص هنا تمنحه القدرة على عرض جميع الحسابات والفحص ومنح التجاوزات المجانية وإلغاء كبار المسؤولين الآخرين.",
  "app.sadmin.addSuperAdmin": "إضافة مسؤول كبير",
  "app.sadmin.mustHaveAccount": "يجب أن يكون لدى المستخدم حساب Netrev بالفعل (سجّل مرة واحدة على الأقل). لا يمكنك إزالة نفسك.",
  "app.sadmin.notesPlaceholder": "ملاحظات (اختياري)",
  "app.sadmin.working": "جارٍ التنفيذ…",
  "app.sadmin.addSuperAdminBtn": "إضافة مسؤول كبير",
  "app.sadmin.currentSuperAdmins": "كبار المسؤولين الحاليون",
  "app.sadmin.errorPrefix": "خطأ:",
  "app.sadmin.colEmail": "البريد الإلكتروني",
  "app.sadmin.colLastSignIn": "آخر تسجيل دخول",
  "app.sadmin.colAdded": "أُضيف",
  "app.sadmin.colNotes": "ملاحظات",
  "app.sadmin.loadingEllipsis": "جارٍ التحميل…",
  "app.sadmin.noSuperAdmins": "لا يوجد كبار مسؤولين.",
  "app.sadmin.never": "أبدًا",
  "app.sadmin.revoke": "إلغاء",

  // ── Team page ────────────────────────────────────────────────────────
  "app.team.title": "الفريق",
  "app.team.descOwner": "ادعُ المشغّلين للمساعدة في إدارة مطاعمك. يمكن للمشغّلين عرض البيانات وإضافة السجلات اليومية / تغييرات الأسعار لمساحات العمل التي تُسندها إليهم. لا يمكنهم رؤية الفوترة أو تغيير الإعدادات.",
  "app.team.descOperator": "أنت تعرض فريق حسابك. يمكن لمالك العلامة التجارية فقط دعوة المشغّلين أو إزالتهم.",
  "app.team.refresh": "تحديث",
  "app.team.teamsYouBelongTo": "الفرق التي تنتمي إليها",
  "app.team.colBrandAccount": "حساب العلامة التجارية",
  "app.team.colYourRole": "دورك",
  "app.team.colInvitedBy": "دُعيت بواسطة",
  "app.team.colWorkspaces": "مساحات العمل",
  "app.team.badgeActive": "نشط",
  "app.team.badgeArchived": "مؤرشف",
  "app.team.all": "الكل",
  "app.team.switchBrandsNote": "بدّل بين حسابات العلامات التجارية باستخدام المُحدِّد في أعلى الشريط الجانبي.",
  "app.team.yourMembership": "عضويتك في الفريق",
  "app.team.labelTeam": "الفريق",
  "app.team.labelInvitedBy": "دُعيت بواسطة",
  "app.team.labelYourRole": "دورك",
  "app.team.roleOperator": "مشغّل",
  "app.team.labelWorkspacesAccess": "مساحات العمل التي يمكنك الوصول إليها",
  "app.team.needAccessPrefix": "تحتاج إلى الوصول إلى مساحة عمل مختلفة؟ اطلب من ",
  "app.team.needAccessFallback": "مالك علامتك التجارية",
  "app.team.needAccessSuffix": " تحديث صلاحياتك.",
  "app.team.inviteOperator": "دعوة مشغّل",
  "app.team.inviteDesc": "يحصل المشغّل على بريد إلكتروني يحتوي على رابط تسجيل الدخول. إذا كان لديه حساب Netrev بالفعل، فسيواصل استخدامه؛ وإن لم يكن، فسينشئ حسابًا. يمكنك أيضًا نسخ رابط الدعوة مباشرةً لمشاركته.",
  "app.team.operatorEmailPlaceholder": "operator@example.com",
  "app.team.workspacesTheyAccess": "مساحات العمل التي يمكنهم الوصول إليها",
  "app.team.noActiveWorkspacesAssign": "لا توجد مساحات عمل نشطة لإسنادها.",
  "app.team.sending": "جارٍ الإرسال…",
  "app.team.sendInvite": "إرسال دعوة",
  "app.team.inviteCreatedFor": "✓ تم إنشاء دعوة لـ",
  "app.team.emailSent": "تم إرسال بريد إلكتروني يحتوي على رابط تسجيل الدخول.",
  "app.team.emailNotSent": "تعذّر إرسال البريد الإلكتروني تلقائيًا — شارك الرابط أدناه مباشرةً.",
  "app.team.copied": "✓ تم النسخ",
  "app.team.copyLink": "نسخ الرابط",
  "app.team.pendingInvites": "الدعوات المعلّقة",
  "app.team.colEmail": "البريد الإلكتروني",
  "app.team.colWorkspacesPlain": "مساحات العمل",
  "app.team.colExpires": "تنتهي",
  "app.team.colStatus": "الحالة",
  "app.team.badgeExpired": "منتهية",
  "app.team.badgePending": "معلّقة",
  "app.team.resend": "إعادة الإرسال",
  "app.team.revoke": "إلغاء",
  "app.team.members": "الأعضاء",
  "app.team.colRole": "الدور",
  "app.team.colWorkspaceAccess": "صلاحية مساحة العمل",
  "app.team.colJoined": "انضم",
  "app.team.colLastSignIn": "آخر تسجيل دخول",
  "app.team.noTeamMembers": "لا يوجد أعضاء فريق.",
  "app.team.roleBrandOwner": "مالك العلامة التجارية",
  "app.team.allWorkspaces": "جميع مساحات العمل",
  "app.team.noAccessSet": "لم تُحدَّد صلاحية",
  "app.team.editAccess": "تعديل الصلاحية",
  "app.team.remove": "إزالة",
  "app.team.editAccessForPrefix": "تعديل صلاحية مساحة العمل لـ",
  "app.team.cancel": "إلغاء",
  "app.team.saving": "جارٍ الحفظ…",
  "app.team.save": "حفظ",

  // ── Accept invite page ───────────────────────────────────────────────
  "app.invite.teamInvite": "دعوة فريق",
  "app.invite.loadingInvite": "جارٍ تحميل الدعوة…",
  "app.invite.cantOpen": "تعذّر فتح هذه الدعوة",
  "app.invite.welcomeToTeam": "مرحبًا بك في الفريق!",
  "app.invite.alreadyOnTeam": "أنت بالفعل في هذا الفريق",
  "app.invite.invitedToPrefix": "لقد دُعيت إلى ",
  "app.invite.invitedToFallback": "حساب",
  "app.invite.returnToNetrev": "العودة إلى Netrev",
  "app.invite.alreadyMemberOf": "أنت بالفعل عضو في ",
  "app.invite.asBrandOwner": " بصفتك مالك العلامة التجارية",
  "app.invite.brandOwnerTestNote": "إذا أردت اختبار دعوة مشغّل، سجّل الخروج أولًا وافتح هذا الرابط في نافذة تصفّح خفي ببريد إلكتروني مختلف.",
  "app.invite.notNeeded": "هذه الدعوة غير ضرورية — يمكنك الوصول إلى هذا الحساب بالفعل.",
  "app.invite.goToDashboard": "الذهاب إلى لوحة التحكم →",
  "app.invite.adminInvitedPrefix": "قام مسؤول (",
  "app.invite.adminInvitedMid": ") بدعوة ",
  "app.invite.adminInvitedSuffix": " للانضمام كمشغّل.",
  "app.invite.invitedAsOperator": "لقد دُعيت كمشغّل في هذا الحساب.",
  "app.invite.ableToViewData": "ستتمكن من عرض البيانات وتحديث السجلات اليومية لمساحات العمل المُسندة إليك.",
  "app.invite.joiningTeam": "جارٍ الانضمام إلى الفريق…",
  "app.invite.acceptAndJoin": "قبول الدعوة والانضمام",
  "app.invite.signInWithEmailPrefix": "سجّل الدخول بالبريد الإلكتروني الذي أُرسلت إليه هذه الدعوة (",
  "app.invite.signInWithEmailSuffix": ") — أو أنشئ حسابًا إن لم يكن لديك واحد بعد.",
  "app.invite.signInToAccept": "سجّل الدخول للقبول →",
  "app.invite.joinedPrefix": "✓ انضممت إلى ",
  "app.invite.joinedSuffix": ". جارٍ إعادة التوجيه…",

  // ── Shared ───────────────────────────────────────────────────────────
  "app.shared.loadingEllipsis": "جارٍ التحميل…",

  // ── Admin view chrome ────────────────────────────────────────────────
  "app.view.brandSwitch": "حساب العلامة التجارية · تبديل",
  "app.view.tapToSwitchBrands": "اضغط للتبديل بين علاماتك التجارية",
  "app.view.operatorSuffix": "(مشغّل)",
  "app.view.archivedSuffix": "— مؤرشف",
  "app.view.workspace": "مساحة العمل",
  "app.view.menu": "القائمة",
  "app.view.createWorkspace": "إنشاء مساحة عمل",
  "app.view.account": "الحساب",
  "app.view.currency": "العملة",
  "app.view.gbpLabel": "£ GBP",
  "app.view.workspaces": "مساحات العمل",
  "app.view.workspaceFallback": "مساحة العمل",
  "app.view.cancelPending": "إلغاء معلّق",
  "app.view.uaeBadge": "🇦🇪 الإمارات",
  "app.view.all": "الكل",
  "app.view.addMenuItem": "+ إضافة صنف للقائمة",
  "app.view.exportCsv": "تصدير CSV",
  "app.view.exportCsvTitle": "تنزيل الصفوف المعروضة كجدول بيانات",
  "app.view.addLogEntry": "+ إضافة إدخال للسجل",
  "app.view.addCpcSpend": "+ إضافة إنفاق تكلفة النقرة",
  "app.view.addCoFund": "+ إضافة مساهمة المنصة",
  "app.view.itemsSuffix": "صنف",
  "app.view.recordsSuffix": "سجل",
  "app.view.noWorkspacesYet": "لا توجد مساحات عمل بعد — أنشئ واحدة في الاشتراكات",
  "app.view.cancellationPendingPrefix": "إلغاء معلّق لـ",
  "app.view.cancellationPendingBody1": "ستُؤرشف مساحة العمل هذه في ",
  "app.view.cancellationPendingFallbackDate": "تاريخ الفوترة التالي",
  "app.view.cancellationPendingBody2": ". لا يزال بإمكانك تعديلها حتى ذلك الحين، أو التراجع عن الإلغاء.",
  "app.view.manage": "إدارة",
  "app.view.welcomeToNetrev": "مرحبًا بك في NETREV",
  "app.view.setUpFirstRestaurant": "لنُعدّ مطعمك الأول",
  "app.view.workspaceWord": "مساحة العمل",
  "app.view.welcomeBody1": "إن ",
  "app.view.welcomeBody2": " هي أحد مواقع مطعمك. هنا ستتمكن من تتبّع أرباحك وخسائرك اليومية عبر جميع المنصات — Deliveroo وUber Eats وTalabat وCareem والمزيد.",
  "app.view.step1Title": "إنشاء مساحة عمل",
  "app.view.step1Desc": "اختر سوقك (المملكة المتحدة أو الإمارات أو الولايات المتحدة أو الهند) وأضف اسم مطعمك",
  "app.view.step2Title": "اضبط أسعارك",
  "app.view.step2Desc": "اضبط نسبة العمولة وضريبة القيمة المضافة وتكلفة البضاعة المباعة لكل منصة توصيل",
  "app.view.step3Title": "تتبّع الربح الحقيقي",
  "app.view.step3Desc": "سجّل المبيعات اليومية وشاهد الهامش الحقيقي بعد كل الرسوم",
  "app.view.noActiveWorkspaces": "لا توجد مساحات عمل نشطة",
  "app.view.noActiveWorkspacesBody": "لديك مساحات عمل مؤرشفة أدناه. استعد إحداها أو أنشئ مساحة عمل جديدة للمتابعة.",
  "app.view.noWorkspaceSelected": "لم تُحدَّد مساحة عمل",
  "app.view.createWsToTrackPrefix": "أنشئ مساحة عمل من ",
  "app.view.subscriptionsWord": "الاشتراكات",
  "app.view.createWsToTrackSuffix": " لبدء تتبّع الأرباح",
  "app.view.createWsToTrackAdmin": "، أو انتقل إلى قسم الإدارة في الشريط الجانبي لإدارة حسابات أخرى.",
  "app.view.createWsToTrackPeriod": ".",
  "app.view.disclaimerLabel": "إخلاء مسؤولية:",
  "app.view.disclaimerBody": " يقدّم Netrev تقديرات لأغراض التخطيط فقط، محسوبة من البيانات والأسعار التي تُدخلها. الأرقام غير مضمونة الدقة وقد تختلف عن كشوفات منصاتك الرسمية. هذه ليست نصيحة مالية أو محاسبية أو ضريبية أو قانونية — تحقّق دائمًا من كشوفات المنصات واستشر مختصًا مؤهلًا قبل اتخاذ قرارات تجارية.",

  // ── Help widget ──────────────────────────────────────────────────────
  "app.help.needHelp": "تحتاج مساعدة؟",
  "app.help.sendMessageReply": "أرسل لنا رسالة وسنرد عبر البريد الإلكتروني.",
  "app.help.thanksReplyPrefix": "شكرًا — سنرد عليك على",
  "app.help.subject": "الموضوع",
  "app.help.subjectPlaceholder": "بماذا تحتاج المساعدة؟",
  "app.help.message": "الرسالة",
  "app.help.messagePlaceholder": "أخبرنا بما يحدث (10 أحرف على الأقل)",
  "app.help.removeTooltip": "إزالة",
  "app.help.attachScreenshots": "إرفاق لقطات شاشة",
  "app.help.sending": "جارٍ الإرسال…",
  "app.help.send": "إرسال",
  "app.help.helpSupportTooltip": "المساعدة والدعم",

  // ── Main API errors ──────────────────────────────────────────────────
  "app.apiErr.bad_request": "تعذّر على Netrev قراءة هذا الطلب. حاول مرة أخرى.",
  "app.apiErr.forbidden": "ليست لديك صلاحية للقيام بذلك.",
  "app.apiErr.not_found": "تعذّر على Netrev العثور على ما طلبته هذه الشاشة. حدّث الصفحة وحاول مرة أخرى.",
  "app.apiErr.method_not_allowed": "تعذّر على Netrev التعامل مع هذا الطلب. حدّث الصفحة وحاول مرة أخرى.",
  "app.apiErr.payload_too_large": "البيانات كثيرة جدًا لإرسالها دفعة واحدة. أرسل أقل وحاول مرة أخرى.",
  "app.apiErr.unsupported_media_type": "تعذّر على Netrev قراءة هذا الطلب. حدّث الصفحة وحاول مرة أخرى.",
  "app.apiErr.rate_limited": "طلبات كثيرة جدًا. انتظر قليلًا ثم حاول مرة أخرى.",
  "app.apiErr.internal_error": "حدث خطأ من جهتنا. حاول مرة أخرى بعد قليل.",
  "app.apiErr.db_unavailable": "تعذّر على Netrev الوصول إلى قاعدة بياناته الآن. حاول مرة أخرى بعد قليل.",
  "app.apiErr.upstream_unavailable": "تعذّر الوصول إلى خدمة يعتمد عليها Netrev الآن. حاول مرة أخرى بعد قليل.",
  "app.apiErr.auth_unavailable": "تعذّر الوصول إلى خدمة تسجيل الدخول الآن. حاول مرة أخرى بعد قليل.",
  "app.apiErr.service_unavailable": "خدمة Netrev غير متاحة الآن. حاول مرة أخرى بعد قليل.",
  "app.apiErr.timeout": "استغرق Netrev وقتًا طويلًا للرد. حاول مرة أخرى.",
  "app.apiErr.unknown": "حدث خطأ ما. حاول مرة أخرى.",
  "app.apiErr.network": "تعذّر الوصول إلى Netrev. تحقّق من اتصالك وحاول مرة أخرى.",

  // ── Recover password ─────────────────────────────────────────────────
  "app.recover.passwordUpdated": "تم تحديث كلمة المرور",
  "app.recover.redirectingSignIn": "جارٍ إعادة توجيهك إلى تسجيل الدخول…",
  "app.recover.setNewPassword": "تعيين كلمة مرور جديدة",
  "app.recover.chooseStrongPassword": "اختر كلمة مرور قوية لم تستخدمها من قبل. 8 أحرف كحد أدنى.",
  "app.recover.newPassword": "كلمة المرور الجديدة",
  "app.recover.atLeast8": "8 أحرف على الأقل",
  "app.recover.confirmNewPassword": "تأكيد كلمة المرور الجديدة",
  "app.recover.repeatPassword": "أعد إدخال كلمة المرور",
  "app.recover.updating": "جارٍ التحديث…",
  "app.recover.updatePassword": "تحديث كلمة المرور",

  // ── Trial banner / subscription status ───────────────────────────────
  "app.trial.endsInPrefix": "تنتهي التجربة خلال",
  "app.trial.freeAccessEndsInPrefix": "ينتهي الوصول المجاني خلال",
  "app.trial.subscribeNow": "اشترك الآن →",
  "app.trial.noCardHint": "لا حاجة لبطاقة الآن",
  "app.trial.noCardUrgentHint": "أضف بطاقة للاحتفاظ بالوصول",
  "app.trial.addCard": "أضف بطاقة →",
  "app.lock.subscribeToUnlock": "اشترك لفتح الميزة",
  "app.lock.priceToolTitle": "أداة التسعير جزء من الخطة المدفوعة",
  "app.lock.priceToolBody": "نمذج الخصومات ورسوم الحملات وهوامش الأصناف بعد الاشتراك. يستمر السجل اليومي وحساب الأرباح خلال الفترة التجريبية.",
  "app.lock.paidOnlyNav": "الخطة المدفوعة",
  "app.admin.funnelTitle": "مسار التسجيل · آخر 30 يومًا",
  "app.admin.funnelUnavailable": "المسار غير متاح بعد — شغّل الترحيل 042.",
  "app.admin.funnel.signedUp": "سجّلوا",
  "app.admin.funnel.confirmed": "أكّدوا البريد",
  "app.admin.funnel.signedIn": "سجّلوا الدخول",
  "app.admin.funnel.firstWorkspace": "أنشأوا مساحة عمل",
  "app.admin.funnel.firstLog": "سجّلوا يومًا واحدًا+",
  "app.admin.funnel.sevenLogs": "سجّلوا 7 أيام+",
  "app.admin.funnel.cardAdded": "بطاقة محفوظة",
  "app.admin.funnel.paid": "يدفعون",
  "app.admin.funnel.eventsPrefix": "لحظات داخل التطبيق:",
  "app.admin.funnel.openedCheckout": "فتحوا صفحة الدفع",
  "app.admin.funnel.sawPaywall": "وصلوا إلى جدار الدفع",
  "app.admin.funnel.uploadedStatement": "رفعوا كشف حساب",
  "app.admin.funnel.eventsPending": "يبدأ تتبع الأحداث بعد تطبيق الترحيل 042",
  "app.admin.funnel.col.email": "البريد",
  "app.admin.funnel.col.region": "المنطقة",
  "app.admin.funnel.col.signedUp": "تاريخ التسجيل",
  "app.admin.funnel.col.stage": "وصل إلى",
  "app.admin.funnel.col.lastSeen": "آخر ظهور",
  "app.admin.funnel.col.logs": "السجلات",
  "app.umodal.addCardKeepTrial": "أضف بطاقة · واحتفظ بتجربتي المجانية →",
  "app.umodal.noChargeUntilPrefix": "لن يتم خصم أي مبلغ اليوم. أول دفعة في ",
  "app.umodal.noChargeUntilSuffix": "، عند انتهاء الفترة التجريبية — يمكنك الإلغاء في أي وقت قبل ذلك.",
  "app.umodal.payNowUnlock": "ادفع الآن بدلًا من ذلك · افتح جميع المنصات اليوم",
  "app.umodal.payNowAlsoLiftsLimit": "الدفع الآن يرفع أيضًا حد الموقع الواحد في الفترة التجريبية ويفتح أداة التسعير وتحليلات الذكاء الاصطناعي. إضافة بطاقة تُبقيها مقفلة حتى انتهاء تجربتك.",
  "app.status.free": "مجاني",
  "app.status.annual": "سنوي",
  "app.status.monthly": "شهري",
  "app.status.trialStartsOnFirstWs": "تبدأ التجربة عند أول مساحة عمل",
  "app.status.trialPrefix": "التجربة:",
  "app.status.paymentFailed": "فشل الدفع",

  // ── Upgrade modal ────────────────────────────────────────────────────
  "app.umodal.subscribeToNetrev": "اشترك في Netrev",
  "app.umodal.pickYourPlan": "اختر خطتك",
  "app.umodal.siteSingular": "موقع",
  "app.umodal.sitePlural": "مواقع",
  "app.umodal.billedIn": "الفوترة بـ",
  "app.umodal.monthly": "شهري",
  "app.umodal.perMonth": "شهريًا",
  "app.umodal.cancelAnytime": "إلغاء في أي وقت",
  "app.umodal.annual": "سنوي",
  "app.umodal.perYear": "سنويًا",
  "app.umodal.twelveMonthCommitment": "التزام لمدة 12 شهرًا",
  "app.umodal.cardOnFileStrong": "ادفع الآن",
  "app.umodal.cardOnFilePrefix": "البطاقة محفوظة بالفعل. ",
  "app.umodal.cardOnFileSuffix": " لإنهاء تجربتك وفتح جميع الميزات فورًا. وإلا ستُفوتر تلقائيًا عند انتهاء التجربة.",
  "app.umodal.unlockedRefreshing": "✓ تم الفتح — جارٍ تحديث مساحة عملك…",
  "app.umodal.activating": "جارٍ تفعيل اشتراكك…",
  "app.umodal.redirectingStripe": "جارٍ التحويل إلى Stripe…",
  "app.umodal.payNowEndTrial": "ادفع الآن · أنهِ التجربة وافتح جميع الميزات →",
  "app.umodal.usingCardOnFile": "باستخدام البطاقة المحفوظة لديك بالفعل. لا حاجة لإعادة التوجيه.",
  "app.umodal.redirectSecureSsl": "ستتم إعادة توجيهك إلى Stripe. دفع آمن عبر تشفير SSL بمعيار 256 بت.",
  "app.umodal.continueToStripe": "المتابعة إلى Stripe →",

  // ── Trial end paywall ────────────────────────────────────────────────
  "app.paywallT.trialEnded": "انتهت التجربة",
  "app.paywallT.chooseWorkspaces": "اختر مساحات عملك",
  "app.paywallT.youCreatedPrefix": "لقد أنشأت ",
  "app.paywallT.workspaceSingular": "مساحة عمل",
  "app.paywallT.workspacePlural": "مساحات عمل",
  "app.paywallT.duringTrialBody": " خلال تجربتك. اختر التي تريد الاحتفاظ بها — وسيُؤرشف الباقي (يمكنك استعادتها خلال 90 يومًا بإعادة الاشتراك).",
  "app.paywallT.freeAccessEnded": "انتهى الوصول المجاني",
  "app.paywallT.duringOverrideBody": " خلال فترة وصولك المجاني. اختر التي تريد الاحتفاظ بها — وسيُؤرشف الباقي (يمكنك استعادتها خلال 90 يومًا بإعادة الاشتراك).",
  "app.paywallT.noWorkspacesToDisplay": "لا توجد مساحات عمل لعرضها.",
  "app.paywallT.untitled": "بدون عنوان",
  "app.paywallT.willBeArchived": "· ستُؤرشف",
  "app.paywallT.monthly": "شهري",
  "app.paywallT.annual": "سنوي",
  "app.paywallT.keepingPrefix": "الاحتفاظ بـ",
  "app.paywallT.willBeArchivedRetention": "ستُؤرشف (احتفاظ لمدة 90 يومًا)",
  "app.paywallT.total": "الإجمالي",
  "app.paywallT.redirectingStripe": "جارٍ التحويل إلى Stripe…",
  "app.paywallT.selectAtLeastOne": "اختر مساحة عمل واحدة على الأقل",
  "app.paywallT.continueToStripe": "المتابعة إلى Stripe →",
  "app.paywallT.cancelTitle": "إلغاء حسابك؟",
  "app.paywallT.cancelBody": "ستُؤرشف جميع مساحات عملك. وسيتم تسجيل خروجك. ستُحفظ بياناتك لمدة 90 يومًا — يمكنك إعادة الاشتراك خلال هذه المدة لاستعادة كل شيء.",
  "app.paywallT.cancelConfirmLabel": "نعم، ألغِ كل شيء",
  "app.paywallT.cancelGoBack": "العودة",
  "app.paywallT.cancelEverything": "إلغاء كل شيء",
  "app.paywallT.signOut": "تسجيل الخروج",
  "app.paywallT.securePaymentNote": "دفع آمن عبر Stripe. يمكنك إلغاء اشتراكك أو تغييره في أي وقت.",

  // ── Paywall screen ───────────────────────────────────────────────────
  "app.paywall.subscribeNow": "اشترك الآن",
  "app.paywall.trialHasEnded": "انتهت تجربتك",
  "app.paywall.trialHasEndedBody": "اشترك للاستمرار في الوصول إلى بياناتك وسجلاتك اليومية ورؤى الذكاء الاصطناعي. بياناتك الحالية آمنة وبانتظارك.",
  "app.paywall.paymentFailed": "فشل الدفع",
  "app.paywall.paymentFailedBody": "تعذّرت معالجة دفعتك الأخيرة — عادةً بسبب بطاقة منتهية الصلاحية أو رصيد غير كافٍ. حدّث طريقة الدفع لاستعادة الوصول. مساحة عملك وجميع بياناتك محفوظة.",
  "app.paywall.updatePaymentMethod": "تحديث طريقة الدفع",
  "app.paywall.paymentNeedsVerification": "الدفع يحتاج إلى تحقق لمرة واحدة",
  "app.paywall.paymentNeedsVerificationBodyINR": "تعذّر تحصيل تجديدك الأخير تلقائيًا — عادةً ما تتطلب البطاقات الصادرة في الهند تحققًا لمرة واحدة عبر رمز OTP قبل تفعيل الدفعات المتكررة. أكمل الدفع على الصفحة الآمنة لإبقاء اشتراكك نشطًا؛ بعد ذلك تُحصَّل التجديدات تلقائيًا. مساحة عملك وجميع بياناتك محفوظة.",
  "app.paywall.completeVerification": "إكمال التحقق",
  "app.paywall.accountArchived": "تم أرشفة الحساب",
  "app.paywall.archivedBody1": "تم إلغاء اشتراكك. نحتفظ ببياناتك لمدة ",
  "app.paywall.archivedBody2": " يوم إضافي",
  "app.paywall.archivedBody2Plural": " أيام إضافية",
  "app.paywall.archivedBodyDays": ". أعد الاشتراك لاستعادة الوصول فورًا.",
  "app.paywall.resubscribe": "إعادة الاشتراك",
  "app.paywall.dataRetentionExpired": "انتهت مدة الاحتفاظ بالبيانات",
  "app.paywall.dataRetentionExpiredBody": "انقضت نافذة الاحتفاظ البالغة 90 يومًا. اشترك للبدء من جديد، أو تواصل مع الدعم إذا كنت بحاجة إلى بيانات سابقة.",
  "app.paywall.subscriptionRequired": "الاشتراك مطلوب",
  "app.paywall.subscriptionRequiredBody": "اشترك للوصول إلى مساحة عمل Netrev الخاصة بك.",
  "app.paywall.reactivateTitle": "إعادة تفعيل اشتراكك؟",
  "app.paywall.subscribingWill": "ستؤدي إعادة الاشتراك إلى:",
  "app.paywall.reactBullet1Strong": "فتح جميع الميزات فورًا",
  "app.paywall.reactBullet1Rest": " — السجلات اليومية ورؤى الذكاء الاصطناعي وتتبّع الأرباح والخسائر",
  "app.paywall.reactBullet2Strong": "بدء الفوترة اليوم",
  "app.paywall.reactBullet2Rest": " — ستُحصَّل رسوم الفترة الأولى من البطاقة المحفوظة لديك",
  "app.paywall.reactBullet3Strong": "استعادة بياناتك المؤرشفة",
  "app.paywall.reactBullet3Rest": " — تعود مساحات العمل والقنوات والسجلات إلى الإنترنت",
  "app.paywall.cancelAnytimeSettings": "يمكنك الإلغاء في أي وقت من إعدادات الفوترة. ",
  "app.paywall.wellTakeYouToStripe": "سننقلك إلى Stripe للتأكيد.",
  "app.paywall.reactConfirmLabel": "نعم، أعد التفعيل",
  "app.paywall.yourAccount": "حسابك",
  "app.paywall.openingStripe": "جارٍ فتح Stripe…",
  "app.paywall.signOut": "تسجيل الخروج",
  "app.paywall.needHelpEmail": "تحتاج مساعدة؟ راسلنا على ",

  // ── Manage billing button ────────────────────────────────────────────
  "app.billing.opening": "جارٍ الفتح…",
  "app.billing.manageBilling": "إدارة الفوترة",

  // ── Top nav plan pills ───────────────────────────────────────────────
  "app.root.superAdmin": "مسؤول كبير",
  "app.root.free": "مجاني",
  "app.root.annual": "سنوي",
  "app.root.monthly": "شهري",
  "app.root.trial": "تجريبي",
  "app.root.pastDue": "متأخر السداد",
  "app.root.canceled": "ملغى",
};
